import { z } from 'zod';
import { isNil } from 'lodash-es';
import { FieldMissingError, InvalidFieldValueError } from '../error.utils';

export const formatName = (name: any): string => (isNil(name) ? '' : `${name}`);

export const parseSchema = <T>(
  schema: z.ZodType<T, any, any>,
  input: unknown,
  detailType: string,
): { data: T } => {
  const result = schema.safeParse(input);
  if (!result.success) {
    const { path, code, message } = result.error.issues[0];
    if (code === 'invalid_type') {
      throw new InvalidFieldValueError({ field: path.join('.'), type: detailType, message });
    }
    throw new FieldMissingError({ value: message, type: detailType });
  }
  return { data: result.data };
};

export const mergeDetailInfo = <T extends { instanceId?: string }>(
  detailInfo: T,
  instanceId?: string,
  addition?: Record<string, any>,
) => {
  return {
    ...detailInfo,
    instanceId: instanceId ?? detailInfo.instanceId,
    ...(addition || {}),
  };
};

/**
 * Zod `z.preprocess`용 팩토리: `null` / `undefined`를 `empty`로 바꿉니다.
 * 그 외 값은 그대로 두어 잘못된 타입은 기존처럼 `z.string()` 등에서 걸리게 합니다.
 *
 * @param empty - nullish일 때 넣을 문자열 (예: `''`)
 */
export const nullishToEmpty =
  (empty: string) =>
  (value: unknown): unknown =>
    isNil(value) ? empty : value;

/** 입력 `null`을 `undefined`로 바꿔 출력 타입에 `null`이 남지 않게 합니다. */
export const nullishAsUndefined = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess((value: unknown) => (value === null ? undefined : value), schema);

const parseNumberOrUndefined = (value: string | number | null | undefined): number | undefined => {
  if (isNil(value) || value === '' || isNaN(+value)) {
    return undefined;
  }
  return +value;
};

export const zodSafeParseNumber = z.preprocess(
  (value: unknown): number | undefined =>
    parseNumberOrUndefined(value as string | number | null | undefined),
  z.number().optional(),
);
